<template>
  <div>
    <h1>Gestão de termos</h1>
    <b-card>
      <b-alert :show="tiposError" variant="danger">
        Não foi possível carregar os tipos de termos.
        <b-button size="sm" @click="reloadTipos">Tentar novamente</b-button>
      </b-alert>
      <b-row>
        <b-form-group label-for="busca-termo" class="col-4">
          <b-form-input
            id="busca-termo"
            v-model="busca"
            type="search"
            placeholder="Buscar termo ou tradução"
          />
        </b-form-group>
        <b-form-group label-for="tipo-termo" class="col-4">
          <b-form-select
            id="tipo-termo"
            v-model="tipo"
            placeholder="Tipo de termo"
            :options="tiposOptions"
            :disabled="tiposLoading"
          />
        </b-form-group>
      </b-row>
      <b-alert :show="itensError" variant="danger">
        Não foi possível carregar os termos. Tente novamente.
        <b-button size="sm" @click="reloadItens">Tentar novamente</b-button>
      </b-alert>
      <b-table
        v-if="!itensError"
        responsive
        outlined
        hover
        show-empty
        :items="itens"
        :fields="fields"
        :busy="tiposLoading || itensLoading"
        :filter="busca"
        :filter-included-fields="['rotulo', 'traducoes']"
        tbody-class="custom-tbody"
        empty-text="Nenhum termo disponível."
        empty-filtered-text="Nenhum termo encontrado para esta busca."
        thead-class="rounded-top"
        @row-clicked="
          (item) =>
            $router.push({
              name: 'termos_filtros_edit',
              params: { tipo, id: item.id },
            })
        "
      >
        <template #cell(traducoes)="{ value }">
          <span
            v-for="(item, key) in value"
            :key="key"
            v-html="item.label"
            class="mr-1 rounded-pill px-2 py-1"
            :class="[
              item.isTranslated
                ? 'bg-success-dark text-white'
                : 'bg-gray bg-gray-5 text-exitos',
            ]"
          />
        </template>
        <template #cell(divider)>
          <font-awesome-icon icon="arrow-right" class="text-gray opacity-5" />
        </template>
        <template #cell(actions)>
          <font-awesome-icon icon="pen" class="text-exitos action-icon" />
        </template>
        <template #table-busy>
          <div class="text-center"><b-spinner label="Carregando termos" /></div>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
export default { name: "TermosFiltrosList" };
</script>

<script setup>
import { computed, ref, watch } from "vue";
import { useQuery } from "@/composables/useQuery";
import services from "@/http";
import { IDIOMAS_LABELS } from "../idiomas";
import { safeArray } from "@/helpers/functions/ArrayHelpers";

const tipo = ref("");
const busca = ref("");

const fields = [
  {
    key: "rotulo",
    label: "Termo",
    sortable: true,
    class: "align-middle h5 mb-0",
  },
  {
    key: "divider",
    label: "",
    class: "col-1",
  },
  {
    key: "traducoes",
    label: "Traduções",
    class: "align-middle",
    formatter: (value) => getIdiomaLabels(value),
  },
  { key: "actions", label: "", class: "align-middle" },
];

const {
  data: catalogo,
  isFetching: tiposLoading,
  isError: tiposError,
  refetch: reloadTipos,
} = useQuery({
  key: ["termos-filtros", "tipos"],
  queryFn: () => services.termos_filtros.get_tipos(),
  select: (response) => response.data,
});

const tiposOptions = computed(() =>
  safeArray(catalogo.value?.tipos).map((item) => ({
    value: item.tipo,
    text: item.label,
  }))
);

const {
  data: resultado,
  isFetching: itensLoading,
  isError: itensError,
  refetch: reloadItens,
} = useQuery({
  key: ["termos-filtros", "itens", tipo],
  queryFn: () =>
    tipo.value
      ? services.termos_filtros.get_itens({ tipo: tipo.value })
      : Promise.resolve({ data: null }),
  select: (response) => response.data,
});

const itens = computed(() =>
  resultado.value?.tipo === tipo.value ? safeArray(resultado.value.itens) : []
);

const idiomas = computed(() => safeArray(catalogo.value?.idiomas));

function getIdiomaLabels(value) {
  const translate = (idioma) => value?.[idioma]?.valor;

  return idiomas.value.map((idioma) => {
    const translated = translate(idioma);
    const label = [
      IDIOMAS_LABELS[idioma] || idioma,
      translated ?? "Sem tradução",
    ].join("  · ");

    return {
      label,
      isTranslated: !!translated,
    };
  });
}

watch(catalogo, (value) => {
  if (!tipo.value) tipo.value = value?.tipos[0]?.tipo || "";
});
</script>
<style scoped lang="scss">
::v-deep .table-hover {
  .custom-tbody {
    cursor: pointer;
    .action-icon {
      opacity: 0;
    }

    [role="row"]:hover {
      background-color: rgba(var(--exitos-rgb), 0.1) !important;

      .action-icon {
        opacity: 1;
      }
    }
  }
}
</style>
